import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';
import {
  Stethoscope, Mail, Lock, User, GraduationCap, Building2, Globe,
  ChevronRight, Check, Eye, EyeOff, ArrowLeft, BookOpen, Award
} from 'lucide-react';
import SocialLoginButton from '../components/SocialLoginButton';
import Icon3D from '../components/Icon3D';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Institutions by Country
const INSTITUTIONS_BY_COUNTRY = {
  "Australia": [
    "Australian National University", "Bond University", "Charles Darwin University",
    "Charles Sturt University", "CQUniversity Australia", "Curtin University",
    "Deakin University", "Edith Cowan University", "Federation University Australia",
    "Flinders University", "Griffith University", "James Cook University",
    "La Trobe University", "Macquarie University", "Monash University",
    "Murdoch University", "Queensland University of Technology", "RMIT University",
    "Southern Cross University", "Swinburne University of Technology",
    "University of Adelaide", "University of Canberra", "University of Melbourne",
    "University of New England", "University of New South Wales",
    "University of Newcastle", "University of Notre Dame Australia",
    "University of Queensland", "University of South Australia",
    "University of Southern Queensland", "University of Sydney",
    "University of Tasmania", "University of Technology Sydney",
    "University of the Sunshine Coast", "University of Western Australia",
    "University of Wollongong", "Victoria University", "Western Sydney University"
  ],
  "New Zealand": [
    "University of Auckland", "University of Otago", "Auckland University of Technology",
    "Victoria University of Wellington", "University of Canterbury", "Massey University",
    "University of Waikato", "Lincoln University"
  ],
  "United Kingdom": [
    "University of Oxford", "University of Cambridge", "Imperial College London",
    "University College London", "King's College London", "University of Edinburgh",
    "University of Manchester", "University of Bristol", "University of Glasgow",
    "University of Birmingham", "University of Leeds", "University of Sheffield",
    "University of Southampton", "University of Liverpool", "University of Nottingham",
    "Newcastle University", "Cardiff University", "Queen's University Belfast",
    "University of Leicester", "University of Dundee", "University of Aberdeen",
    "St George's University of London", "Brighton and Sussex Medical School",
    "Hull York Medical School", "Keele University", "Lancaster University",
    "Plymouth University", "University of Exeter", "University of Warwick",
    "Queen Mary University of London"
  ],
  "United States": [
    "Harvard Medical School", "Johns Hopkins University", "Stanford University",
    "University of California, San Francisco", "University of Pennsylvania",
    "Columbia University", "Duke University", "Yale University",
    "University of Michigan", "University of Washington", "Northwestern University",
    "University of Chicago", "Cornell University", "University of Pittsburgh",
    "Vanderbilt University", "University of California, Los Angeles",
    "University of California, San Diego", "New York University", "Emory University",
    "University of North Carolina", "Washington University in St. Louis",
    "Baylor College of Medicine", "University of Virginia", "University of Wisconsin",
    "Ohio State University", "University of Texas Southwestern",
    "University of Colorado", "University of Minnesota", "University of Florida",
    "Boston University"
  ],
  "Canada": [
    "University of Toronto", "McGill University", "University of British Columbia",
    "McMaster University", "University of Alberta", "University of Ottawa",
    "Queen's University", "Western University", "University of Calgary",
    "Dalhousie University", "University of Manitoba", "University of Saskatchewan",
    "Memorial University of Newfoundland", "Northern Ontario School of Medicine",
    "University of Montreal", "Laval University", "University of Sherbrooke"
  ],
  "Ireland": [
    "Trinity College Dublin", "University College Dublin",
    "Royal College of Surgeons in Ireland", "University College Cork",
    "National University of Ireland, Galway", "University of Limerick"
  ],
  "Singapore": [
    "National University of Singapore", "Duke-NUS Medical School",
    "Nanyang Technological University", "Lee Kong Chian School of Medicine"
  ],
  "Malaysia": [
    "University of Malaya", "Universiti Kebangsaan Malaysia",
    "Universiti Putra Malaysia", "Universiti Sains Malaysia",
    "International Medical University", "Monash University Malaysia",
    "Taylor's University", "UCSI University", "Management and Science University",
    "Perdana University"
  ],
  "India": [
    "All India Institute of Medical Sciences (AIIMS), New Delhi",
    "Christian Medical College, Vellore", "Armed Forces Medical College, Pune",
    "Maulana Azad Medical College, Delhi", "King George's Medical University, Lucknow",
    "Jawaharlal Institute of Postgraduate Medical Education & Research",
    "Grant Medical College, Mumbai", "Seth GS Medical College, Mumbai",
    "Kasturba Medical College, Manipal", "St. John's Medical College, Bangalore",
    "Lady Hardinge Medical College, Delhi", "Madras Medical College, Chennai",
    "BJ Medical College, Ahmedabad", "Institute of Medical Sciences, BHU",
    "Government Medical College, Chandigarh"
  ],
  "Other": []
};

// Degree Types by Country
const DEGREE_TYPES_BY_COUNTRY = {
  "Australia": [
    "Bachelor of Medicine / Bachelor of Surgery (MBBS)", "Doctor of Medicine (MD)",
    "Bachelor of Medical Science", "Graduate Entry Medicine",
    "Bachelor of Clinical Sciences", "Other Medical Degree"
  ],
  "New Zealand": [
    "Bachelor of Medicine and Bachelor of Surgery (MBChB)",
    "Bachelor of Medical Science", "Graduate Entry Medicine", "Other Medical Degree"
  ],
  "United Kingdom": [
    "Bachelor of Medicine, Bachelor of Surgery (MBBS/MBChB)",
    "Bachelor of Medicine (BM)", "Graduate Entry Medicine (GEM)",
    "Bachelor of Medical Sciences (BMedSci)", "Other Medical Degree"
  ],
  "United States": [
    "Doctor of Medicine (MD)", "Doctor of Osteopathic Medicine (DO)",
    "Bachelor of Science in Medicine", "Pre-Medical Studies", "Other Medical Degree"
  ],
  "Canada": [
    "Doctor of Medicine (MD)", "Doctor of Medicine and Master of Surgery (MDCM)",
    "Bachelor of Medical Sciences", "Other Medical Degree"
  ],
  "Ireland": [
    "Bachelor of Medicine, Bachelor of Surgery (MB BCh BAO)",
    "Graduate Entry Medicine", "Bachelor of Medical Science", "Other Medical Degree"
  ],
  "Singapore": [
    "Bachelor of Medicine and Bachelor of Surgery (MBBS)",
    "Doctor of Medicine (MD)", "Graduate Entry Medicine", "Other Medical Degree"
  ],
  "Malaysia": [
    "Bachelor of Medicine and Bachelor of Surgery (MBBS)",
    "Doctor of Medicine (MD)", "Bachelor of Medical Science", "Other Medical Degree"
  ],
  "India": [
    "Bachelor of Medicine and Bachelor of Surgery (MBBS)",
    "Bachelor of Dental Surgery (BDS)",
    "Bachelor of Ayurvedic Medicine and Surgery (BAMS)",
    "Bachelor of Homeopathic Medicine and Surgery (BHMS)", "Other Medical Degree"
  ],
  "Other": [
    "Bachelor of Medicine / Bachelor of Surgery (MBBS)",
    "Doctor of Medicine (MD)", "Graduate Entry Medicine", "Other Medical Degree"
  ]
};

const COUNTRIES = [
  "Australia", "New Zealand", "United Kingdom", "United States",
  "Canada", "Ireland", "Singapore", "Malaysia", "India", "Other"
];

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [institution, setInstitution] = useState('');
  const [customInstitution, setCustomInstitution] = useState('');
  const [currentYear, setCurrentYear] = useState('');
  const [degreeType, setDegreeType] = useState('');
  const [country, setCountry] = useState('Australia');
  const [gender, setGender] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const [availableInstitutions, setAvailableInstitutions] = useState(INSTITUTIONS_BY_COUNTRY["Australia"]);
  const [availableDegreeTypes, setAvailableDegreeTypes] = useState(DEGREE_TYPES_BY_COUNTRY["Australia"]);

  const { login, register } = useAuth();
  const { tenant, getPrimaryColor } = useTenant();
  const navigate = useNavigate();

  const tenantName = tenant?.name || 'MedMCQ';
  const tenantTagline = tenant?.tagline || 'Medical Student Learning Platform';
  const primaryColor = getPrimaryColor() || '#0891b2';

  useEffect(() => {
    document.title = `Login | ${tenantName}`;
  }, [tenantName]);

  useEffect(() => {
    const institutions = INSTITUTIONS_BY_COUNTRY[country] || [];
    const degreeTypes = DEGREE_TYPES_BY_COUNTRY[country] || DEGREE_TYPES_BY_COUNTRY["Other"];
    setAvailableInstitutions(institutions);
    setAvailableDegreeTypes(degreeTypes);
    setInstitution('');
    setDegreeType('');
    setCustomInstitution('');
  }, [country]);

  const clearError = () => {
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        if (!marketingConsent) {
          setError('You must accept the marketing communications disclaimer to create a free account.');
          setLoading(false);
          return;
        }
        const finalInstitution = institution === 'Other' ? customInstitution : institution;
        result = await register(email, password, fullName, {
          institution: finalInstitution,
          current_year: currentYear ? parseInt(currentYear) : null,
          degree_type: degreeType,
          country: country,
          gender: gender || undefined,
          marketing_consent: marketingConsent
        });
      }

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setForgotLoading(true);

    if (!forgotEmail) {
      setForgotMessage('Please enter your email address.');
      setForgotLoading(false);
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotMessage('If an account exists with this email, you will receive password reset instructions shortly.');
    } catch (error) {
      setForgotMessage('If an account exists with this email, you will receive password reset instructions shortly.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex">
        {/* Left Panel */}
        <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 animate-pulse" />
          </div>
          <div className="relative z-10 flex flex-col justify-center px-12 max-w-lg">
            <div className="flex items-center gap-4 mb-12">
              <Icon3D icon={Stethoscope} colour="turquoise" size="lg" />
              <span className="text-2xl font-bold text-white">
                Med<span className="text-cyan-400">MCQ</span>
              </span>
            </div>
            <h1 className="text-4xl font-bold text-white leading-tight mb-6">
              Reset Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-300">Password</span>
            </h1>
            <p className="text-lg text-white/70 mb-8">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="flex-1 flex items-center justify-center p-6 bg-white dark:bg-slate-900">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
              <Icon3D icon={Stethoscope} colour="turquoise" size="md" />
              <span className="text-xl font-bold text-slate-900 dark:text-white">
                Med<span className="text-cyan-500">MCQ</span>
              </span>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Forgot Password</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8">Enter your email to receive reset instructions</p>

            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {forgotMessage && (
                <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 text-cyan-700 dark:text-cyan-300 rounded-xl text-sm">
                  {forgotMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={forgotLoading}
                className="w-full py-4 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Instructions'}
                {!forgotLoading && <ChevronRight className="w-5 h-5" />}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotEmail('');
                  setForgotMessage('');
                }}
                className="w-full py-3 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 animate-pulse" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-12 py-8 max-w-xl">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-8">
            <Icon3D icon={Stethoscope} colour="turquoise" size="lg" />
            <span className="text-4xl font-bold text-white">
              Med<span className="text-cyan-400">MCQ</span>
            </span>
          </div>

          {/* Tagline */}
          <h1 className="text-3xl font-bold text-white leading-tight mb-4">
            Master Medicine Through <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-300">Smart Practice</span>
          </h1>

          <p className="text-base text-white/70 mb-6">
            Prepare for your medical exams with our comprehensive MCQ bank,
            adaptive learning, and detailed explanations created by medical professionals.
          </p>

          {/* Features */}
          <div className="space-y-3 mb-6">
            {[
              '95,000+ practice questions across all specialties',
              'Detailed explanations by medical professionals',
              'Adaptive learning that focuses on your weak areas',
              'Track your progress with detailed analytics'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-white/90">
                <Icon3D icon={Check} colour="turquoise" size="xs" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="flex gap-10 pt-6 border-t border-white/10">
            <div className="flex items-center gap-3">
              <Icon3D icon={BookOpen} colour="blue" size="sm" />
              <div>
                <div className="text-2xl font-bold text-white">95,000+</div>
                <div className="text-xs text-white/60">Practice Questions</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Icon3D icon={Award} colour="orange" size="sm" />
              <div>
                <div className="text-2xl font-bold text-white">95%</div>
                <div className="text-xs text-white/60">Pass Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white dark:bg-slate-900 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <Icon3D icon={Stethoscope} colour="turquoise" size="md" />
            <span className="text-xl font-bold text-slate-900 dark:text-white">
              Med<span className="text-cyan-500">MCQ</span>
            </span>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {isLogin ? 'Welcome back' : 'Create an account'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-cyan-500 font-semibold hover:underline"
              >
                {isLogin ? 'Sign up free' : 'Log in'}
              </button>
            </p>
          </div>

          {/* Tabs */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all ${
                isLogin ? 'bg-white dark:bg-slate-700 text-cyan-500 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              Log in
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all ${
                !isLogin ? 'bg-white dark:bg-slate-700 text-cyan-500 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white"
                      placeholder="John Smith"
                      required
                    />
                  </div>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Country *</label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white appearance-none bg-white"
                      required
                    >
                      {COUNTRIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Institution */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Institution *</label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    {availableInstitutions.length > 0 ? (
                      <select
                        value={institution}
                        onChange={(e) => setInstitution(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white appearance-none bg-white"
                        required
                      >
                        <option value="">Select your institution</option>
                        {availableInstitutions.map(uni => (
                          <option key={uni} value={uni}>{uni}</option>
                        ))}
                        <option value="Other">Other</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={customInstitution}
                        onChange={(e) => setCustomInstitution(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white"
                        placeholder="Enter your institution"
                        required
                      />
                    )}
                  </div>
                </div>

                {/* Custom Institution */}
                {institution === 'Other' && availableInstitutions.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Institution Name *</label>
                    <input
                      type="text"
                      value={customInstitution}
                      onChange={(e) => setCustomInstitution(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white"
                      placeholder="Enter your institution name"
                      required
                    />
                  </div>
                )}

                {/* Degree Type */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Degree Type *</label>
                  <div className="relative">
                    <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      value={degreeType}
                      onChange={(e) => setDegreeType(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white appearance-none bg-white"
                      required
                    >
                      <option value="">Select degree type</option>
                      {availableDegreeTypes.map(degree => (
                        <option key={degree} value={degree}>{degree}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Year of Study */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Year of Study *</label>
                  <select
                    value={currentYear}
                    onChange={(e) => setCurrentYear(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white appearance-none bg-white"
                    required
                  >
                    <option value="">Select year</option>
                    {[1, 2, 3, 4, 5, 6].map(y => (
                      <option key={y} value={y}>Year {y}</option>
                    ))}
                    <option value="7">Year 7+</option>
                  </select>
                </div>

                {/* Gender (optional) */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Gender <span className="text-slate-400 font-normal">(optional)</span></label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white appearance-none bg-white"
                  >
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Email Address *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={clearError}
                  className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white"
                  placeholder="you@university.edu"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Password *</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={clearError}
                  className="w-full pl-12 pr-12 py-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all bg-white dark:bg-slate-700 dark:text-white"
                  placeholder={isLogin ? 'Enter your password' : 'Create a strong password'}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {!isLogin && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Minimum 6 characters</p>}
            </div>

            {/* Login extras */}
            {isLogin && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-cyan-500 focus:ring-cyan-500" />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-cyan-500 font-semibold hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Marketing Consent */}
            {!isLogin && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-cyan-500 focus:ring-cyan-500"
                    required
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-200">
                    <span className="font-semibold">Marketing Communications *</span>
                    <br />
                    <span className="text-xs text-slate-600 dark:text-slate-400">
                      By creating a free account, I consent to receive promotional emails from {tenantName}. I can unsubscribe at any time.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || (!isLogin && !marketingConsent)}
              className="w-full py-4 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
              <span className="relative z-10">{loading ? 'Please wait...' : isLogin ? 'Log in' : 'Create Account'}</span>
              {!loading && <ChevronRight className="w-5 h-5 relative z-10" />}
            </button>
          </form>

          {/* Social login — only rendered when provider credentials are configured */}
          {(process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_APPLE_CLIENT_ID || process.env.REACT_APP_FACEBOOK_APP_ID) && (
            <div className="mt-6">
              <div className="relative flex items-center my-4">
                <div className="flex-grow border-t border-slate-200" />
                <span className="mx-3 text-sm text-slate-400 font-medium whitespace-nowrap">Or continue with</span>
                <div className="flex-grow border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="flex justify-center gap-3">
                <SocialLoginButton provider="google" variant="compact" />
                <SocialLoginButton provider="apple" variant="compact" />
                <SocialLoginButton provider="facebook" variant="compact" />
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              By continuing, you agree to our{' '}
              <a href="#" className="text-cyan-500 hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-cyan-500 hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
